import {
  BoxV2 as Box,
  Button,
  ColorInput,
  Flex,
  TextInput,
} from 'portal-commons';
import {
  CSSProperties,
  FunctionComponent,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Controller, useForm } from 'react-hook-form';
import { faBullhorn, faFileImage } from '@fortawesome/pro-regular-svg-icons';

import AddButton from './AddButton';
import PhoneInput from './PhoneInput';
import RemoveButton from './RemoveButton';
import AssetUploadModal from '../AssetUploadModal';
import AssetVerificationModal from '../AssetVerificationModal';
import AssetVerificationPendingModal from '../AssetVerificationPendingModal';
import CampaignAsset from '../CampaignAsset';
import {
  FormActionLayout,
  FormContainerLayout,
  FormGridLayout,
} from '../FormLayout';
import FormSection from '../FormSection';
import { useControllers } from './hooks';
import { RcsCampaignDetailsFormValues } from './types';
import {
  createEmailValidator,
  createMaxLengthValidator,
  createWebsiteValidator,
  isRequired,
  isValidInput,
} from '../../validators';
import { BrandAsset, brandAssetController, BrandAssetType } from '@/apis/core';
import {
  DRAFT_RCS_CAMPAIGN_ENABLED,
  ASSET_LINK_UPLOAD_ENABLED,
} from '@/constants';
import { AssetUploadModal as AssetImageLinkUploadModal } from '@/components';

const MaxPairCount = 3;

interface RcsCampaignDetailsFormProps {
  brandUid: string;
  defaultValues?: RcsCampaignDetailsFormValues;
  onChange?: (values: RcsCampaignDetailsFormValues) => void;
  onPrevious?: () => void;
  onSave?: () => void;
  onNext?: () => void;
}

enum ModalType {
  None = 'none',
  AssetVerification = 'assetVerification',
  AssetVerificationPending = 'assetVerificationPending',
  LogoUpload = 'logoUpload',
  BannerUpload = 'bannerUpload',
}

interface CampaignAssetLayoutProps {
  style?: CSSProperties;
  assetWidth?: number | string;
}

const CampaignAssetLayout: FunctionComponent<CampaignAssetLayoutProps> = ({
  style,
  assetWidth = '154px',
  children,
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(auto-fill, ${assetWidth})`,
      gap: '17px',
      height: '216px',
      overflow: 'auto',
      ...style,
    }}
  >
    {children}
  </Box>
);

const isValidAsset = (asset: BrandAsset) =>
  !asset.verificationStatus ||
  asset.verificationStatus === 'PENDING' ||
  asset.verificationStatus === 'VERIFIED';

const RcsCampaignDetailsForm: FunctionComponent<
  RcsCampaignDetailsFormProps
> = ({ brandUid, defaultValues, onChange, onPrevious, onSave, onNext }) => {
  const {
    formState: { isSubmitting, errors },
    handleSubmit,
    control,
    watch,
  } = useForm<RcsCampaignDetailsFormValues>({
    defaultValues,
  });
  const {
    displayName,
    setDisplayName,
    validateDisplayName,
    displayNameError,
    color,
    setColor,
    colorError,
    shortDescription,
    setShortDescription,
    validateShortDescription,
    shortDescriptionError,
    termsAndConditionsLink,
    setTermsAndConditionsLink,
    validateTermsAndConditionsLink,
    termsAndConditionsLinkError,
    privacyPolicyLink,
    setPrivacyPolicyLink,
    validatePrivacyPolicyLink,
    privacyPolicyLinkError,
    phonePairs,
    addPhonePair,
    removePhonePair,
    emailPairs,
    addEmailPair,
    removeEmailPair,
    websitePairs,
    addWebsitePair,
    removeWebsitePair,
    campaignLogo,
    setCampaignLogo,
    campaignLogoError,
    campaignBanner,
    setCampaignBanner,
    campaignBannerError,
  } = useControllers(control);
  const [assets, setAssets] = useState<BrandAsset[]>([]);
  const [activeAsset, setActiveAsset] = useState<BrandAsset>();
  const [modalType, setModalType] = useState(ModalType.None);

  const campaignLogos = useMemo(
    () =>
      assets.filter((asset) => asset.type === 'LOGO' && isValidAsset(asset)),
    [assets]
  );
  const campaignBanners = useMemo(
    () =>
      assets.filter((asset) => asset.type === 'BANNER' && isValidAsset(asset)),
    [assets]
  );

  const assetUploadDialogDescription = useMemo(() => {
    if (
      modalType === ModalType.LogoUpload ||
      modalType === ModalType.BannerUpload
    ) {
      const type = modalType === ModalType.LogoUpload ? 'logo' : 'banner';
      return (
        <>
          This asset has not been verified. Verification is required for use in
          an RCS campaign.
          <br />
          <br />
          <>
            A one-time, non-refundable charge of{' '}
            {type === 'logo' ? '$75' : '$50'} is required to verify a {type} and
            will be added to your balance.
          </>
          <br />
          <br />
          Click{' '}
          <Box as="span" sx={{ fontWeight: 600 }}>
            I Agree
          </Box>{' '}
          to proceed.
        </>
      );
    }
  }, [modalType]);

  const assetUploadPanelDescription = useMemo(() => {
    if (
      modalType === ModalType.LogoUpload ||
      modalType === ModalType.BannerUpload
    ) {
      return (
        <>
          Only upload official trademarked{' '}
          {modalType === ModalType.LogoUpload ? 'logos' : 'banners'} of the
          brand for use in RCS campaigns.
          <br /> Image must be{' '}
          {modalType === ModalType.LogoUpload ? '224x224' : '1440x448'}
          px.
        </>
      );
    }
  }, [modalType]);

  const reloadBrandAssets = async () => {
    const newAssets = await brandAssetController.getBrandAssets(brandUid);
    setAssets(newAssets);
  };

  const handleValid = () => {
    onNext?.();
  };

  const handleLogoUpload = () => {
    setModalType(ModalType.LogoUpload);
  };

  const handleBannerUpload = () => {
    setModalType(ModalType.BannerUpload);
  };

  useEffect(() => {
    reloadBrandAssets();
  }, []);

  useEffect(() => {
    const { unsubscribe } = watch((values) => {
      onChange?.(values as RcsCampaignDetailsFormValues);
    });
    return () => unsubscribe();
  }, [watch]);

  return (
    <form noValidate onSubmit={handleSubmit(handleValid)}>
      <FormContainerLayout>
        <FormSection
          icon={faBullhorn}
          title="Campaign Details"
          message="Enter the details for your RCS campaign."
          sx={{ paddingBottom: 45 }}
        >
          <Flex sx={{ flexDirection: 'column', gap: '47px' }}>
            <FormGridLayout rowGap="ssx">
              <TextInput
                error={!!displayNameError?.message}
                label="Display Name"
                note={displayNameError?.message}
                maxLength={40}
                value={displayName}
                onChange={setDisplayName}
                onBlur={validateDisplayName}
                data-testid="displayName"
              />
              <Box
                sx={{
                  gridRow: '1 / span 2',
                  gridColumn: '2',
                  '& textarea': {
                    minHeight: '91px',
                    maxHeight: '91px',
                    overflow: 'auto !important',
                  },
                }}
              >
                <TextInput
                  multiline
                  error={!!shortDescriptionError?.message}
                  label="Short Description"
                  note={shortDescriptionError?.message}
                  maxLength={100}
                  value={shortDescription}
                  onChange={setShortDescription}
                  onBlur={validateShortDescription}
                  data-testid="shortDescription"
                />
              </Box>
              <ColorInput
                error={!!colorError?.message}
                label="Color"
                note={colorError?.message}
                value={color}
                onChange={setColor}
                data-testid="color"
              />
            </FormGridLayout>
            <FormGridLayout>
              <TextInput
                error={!!termsAndConditionsLinkError?.message}
                label="Terms of Use URL"
                note={termsAndConditionsLinkError?.message}
                value={termsAndConditionsLink}
                onChange={setTermsAndConditionsLink}
                onBlur={validateTermsAndConditionsLink}
                data-testid="termsAndConditionsLink"
              />
              <TextInput
                error={!!privacyPolicyLinkError?.message}
                label="Privacy Policy URL"
                note={privacyPolicyLinkError?.message}
                value={privacyPolicyLink}
                onChange={setPrivacyPolicyLink}
                onBlur={validatePrivacyPolicyLink}
                data-testid="privacyPolicyLink"
              />
            </FormGridLayout>
            <FormGridLayout rowGap="28px">
              {phonePairs.map((phonePair, index) => (
                <>
                  <Controller
                    key={`phonePairs.${phonePair.timestamp}.phone`}
                    control={control}
                    name={`phonePairs.${index}.phone`}
                    rules={{
                      validate: {
                        isRequired,
                        isValidInput,
                        withValidMaxLength: createMaxLengthValidator(20),
                      },
                    }}
                    render={({
                      field: { value, onChange, onBlur },
                      fieldState: { error },
                    }) => (
                      <Flex
                        sx={{
                          position: 'relative',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '10px',
                          height: 'fit-content',
                          marginTop: index > 0 ? '-12px' : 0,
                        }}
                      >
                        {index > 0 && (
                          <RemoveButton
                            onClick={() => removePhonePair(index)}
                          />
                        )}
                        <PhoneInput
                          style={{ flex: 1 }}
                          error={!!error?.message}
                          placeholder={`${
                            index < 1 ? 'Primary ' : ''
                          }Phone Number`}
                          note={error?.message}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          data-testid={`phones[${index}]`}
                        />
                        {phonePairs.length < MaxPairCount &&
                          index === phonePairs.length - 1 && (
                            <AddButton
                              onClick={() =>
                                addPhonePair({
                                  phone: '',
                                  label: '',
                                  timestamp: Date.now(),
                                })
                              }
                            >
                              Add Phone Number
                            </AddButton>
                          )}
                      </Flex>
                    )}
                  />
                  <Controller
                    key={`phonePairs.${phonePair.timestamp}.label`}
                    control={control}
                    name={`phonePairs.${index}.label`}
                    rules={{
                      validate: {
                        isRequired,
                        isValidInput,
                        withValidMaxLength: createMaxLengthValidator(25),
                      },
                    }}
                    render={({
                      field: { value, onChange, onBlur },
                      fieldState: { error },
                    }) => (
                      <Box
                        sx={{
                          marginTop: index > 0 ? '-12px' : 0,
                        }}
                      >
                        <TextInput
                          error={!!error?.message}
                          label={`Label for ${
                            index < 1 ? 'Primary ' : ''
                          }Phone Number`}
                          note={error?.message}
                          maxLength={25}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          data-testid={`phoneLabels[${index}]`}
                        />
                      </Box>
                    )}
                  />
                </>
              ))}
              {emailPairs.map((emailPair, index) => (
                <>
                  <Controller
                    key={`emailPairs.${emailPair.timestamp}.email`}
                    control={control}
                    name={`emailPairs.${index}.email`}
                    rules={{
                      validate: {
                        isRequired,
                        withValidEmail: createEmailValidator('Invalid Email'),
                      },
                    }}
                    render={({
                      field: { value, onChange, onBlur },
                      fieldState: { error },
                    }) => (
                      <Flex
                        sx={{
                          position: 'relative',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '10px',
                          height: 'fit-content',
                          marginTop: index > 0 ? '-12px' : 0,
                        }}
                      >
                        {index > 0 && (
                          <RemoveButton
                            onClick={() => removeEmailPair(index)}
                          />
                        )}
                        <TextInput
                          style={{ flex: 1 }}
                          error={!!error?.message}
                          label={`${index < 1 ? 'Primary ' : ''}Email`}
                          note={error?.message}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          data-testid={`emails[${index}]`}
                        />
                        {emailPairs.length < MaxPairCount &&
                          index === emailPairs.length - 1 && (
                            <AddButton
                              onClick={() =>
                                addEmailPair({
                                  email: '',
                                  label: '',
                                  timestamp: Date.now(),
                                })
                              }
                            >
                              Add Email
                            </AddButton>
                          )}
                      </Flex>
                    )}
                  />
                  <Controller
                    key={`emailPairs.${emailPair.timestamp}.label`}
                    control={control}
                    name={`emailPairs.${index}.label`}
                    rules={{
                      validate: {
                        isRequired,
                        isValidInput,
                        withValidMaxLength: createMaxLengthValidator(25),
                      },
                    }}
                    render={({
                      field: { value, onChange, onBlur },
                      fieldState: { error },
                    }) => (
                      <Box
                        sx={{
                          marginTop: index > 0 ? '-12px' : 0,
                        }}
                      >
                        <TextInput
                          error={!!error?.message}
                          label={`Label for ${
                            index < 1 ? 'Primary ' : ''
                          }Email`}
                          note={error?.message}
                          maxLength={25}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          data-testid={`emailLabels[${index}]`}
                        />
                      </Box>
                    )}
                  />
                </>
              ))}
              {websitePairs.map((websitePair, index) => (
                <>
                  <Controller
                    key={`websitePairs.${websitePair.timestamp}.website`}
                    control={control}
                    name={`websitePairs.${index}.website`}
                    rules={{
                      validate: {
                        isRequired,
                        withValidUrl: createWebsiteValidator('Invalid Website'),
                      },
                    }}
                    render={({
                      field: { value, onChange, onBlur },
                      fieldState: { error },
                    }) => (
                      <Flex
                        sx={{
                          position: 'relative',
                          flexDirection: 'row',
                          alignItems: 'center',
                          gap: '10px',
                          height: 'fit-content',
                          marginTop: index > 0 ? '-12px' : 0,
                        }}
                      >
                        {index > 0 && (
                          <RemoveButton
                            onClick={() => removeWebsitePair(index)}
                          />
                        )}
                        <TextInput
                          style={{ flex: 1 }}
                          error={!!error?.message}
                          label={`${index < 1 ? 'Primary ' : ''}Website`}
                          note={error?.message}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          data-testid={`websites[${index}]`}
                        />
                        {websitePairs.length < MaxPairCount &&
                          index === websitePairs.length - 1 && (
                            <AddButton
                              onClick={() =>
                                addWebsitePair({
                                  website: '',
                                  label: '',
                                  timestamp: Date.now(),
                                })
                              }
                            >
                              Add Website
                            </AddButton>
                          )}
                      </Flex>
                    )}
                  />
                  <Controller
                    key={`websitePairs.${websitePair.timestamp}.label`}
                    control={control}
                    name={`websitePairs.${index}.label`}
                    rules={{
                      validate: {
                        isRequired,
                        isValidInput,
                        withValidMaxLength: createMaxLengthValidator(25),
                      },
                    }}
                    render={({
                      field: { value, onChange, onBlur },
                      fieldState: { error },
                    }) => (
                      <Box
                        sx={{
                          marginTop: index > 0 ? '-12px' : 0,
                        }}
                      >
                        <TextInput
                          error={!!error?.message}
                          label={`Label for ${
                            index < 1 ? 'Primary ' : ''
                          }Website`}
                          note={error?.message}
                          maxLength={25}
                          value={value}
                          onChange={onChange}
                          onBlur={onBlur}
                          data-testid={`websiteLabels[${index}]`}
                        />
                      </Box>
                    )}
                  />
                </>
              ))}
            </FormGridLayout>
          </Flex>
        </FormSection>
        <FormSection
          icon={faFileImage}
          title="Campaign Logo"
          message="Select a logo from your brand assets to use for your campaign."
          action={
            <Button
              style={{ padding: '8px 12px', borderRadius: '4px' }}
              color="secondary"
              shape="square"
              size="small"
              onClick={handleLogoUpload}
            >
              Upload Logo
            </Button>
          }
          error={campaignLogoError?.message}
        >
          <Flex sx={{ flexDirection: 'column', gap: 'xxs' }}>
            <CampaignAssetLayout>
              {campaignLogos.map((logo) => (
                <CampaignAsset
                  selected={logo.attachmentUuid === campaignLogo}
                  type={logo.type}
                  name={logo.name}
                  status={logo.verificationStatus}
                  uuid={logo.attachmentUuid}
                  onSelect={() => {
                    if (logo.verificationStatus === 'PENDING') {
                      setActiveAsset(logo);
                      setModalType(ModalType.AssetVerificationPending);
                    } else {
                      setCampaignLogo(logo.attachmentUuid);
                    }
                  }}
                  onVerify={() => {
                    setActiveAsset(logo);
                    setModalType(ModalType.AssetVerification);
                  }}
                  data-testid={logo.attachmentUuid}
                />
              ))}
            </CampaignAssetLayout>
          </Flex>
        </FormSection>
        <FormSection
          icon={faFileImage}
          title="Campaign Banner"
          message="Select a banner from your brand assets to use for your campaign."
          action={
            <Button
              style={{ padding: '8px 12px', borderRadius: '4px' }}
              color="secondary"
              shape="square"
              size="small"
              onClick={handleBannerUpload}
            >
              Upload Banner
            </Button>
          }
          error={campaignBannerError?.message}
        >
          <Flex sx={{ flexDirection: 'column', gap: '4px' }}>
            <CampaignAssetLayout style={{ height: '274px' }} assetWidth="379px">
              {campaignBanners.map((banner) => (
                <CampaignAsset
                  selected={banner.attachmentUuid === campaignBanner}
                  type={banner.type}
                  name={banner.name}
                  status={banner.verificationStatus}
                  uuid={banner.attachmentUuid}
                  onSelect={() => {
                    if (banner.verificationStatus === 'PENDING') {
                      setActiveAsset(banner);
                      setModalType(ModalType.AssetVerificationPending);
                    } else {
                      setCampaignBanner(banner.attachmentUuid);
                    }
                  }}
                  onVerify={() => {
                    setActiveAsset(banner);
                    setModalType(ModalType.AssetVerification);
                  }}
                  data-testid={banner.attachmentUuid}
                />
              ))}
            </CampaignAssetLayout>
          </Flex>
        </FormSection>
        <FormActionLayout>
          <Button
            disabled={isSubmitting}
            variant="outline"
            onClick={onPrevious}
          >
            Previous
          </Button>
          {DRAFT_RCS_CAMPAIGN_ENABLED && (
            <Button disabled={isSubmitting} variant="outline" onClick={onSave}>
              Save Draft
            </Button>
          )}
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              Object.values(errors).some((error) => !!error?.message)
            }
          >
            Next
          </Button>
        </FormActionLayout>
      </FormContainerLayout>
      <AssetVerificationModal
        open={modalType === ModalType.AssetVerification}
        brandUid={brandUid}
        type={activeAsset?.type}
        name={activeAsset?.name}
        uuid={activeAsset?.attachmentUuid}
        onCancel={() => {
          setModalType(ModalType.None);
          setActiveAsset(undefined);
        }}
        onSubmit={() => {
          reloadBrandAssets();
          setActiveAsset(undefined);
          setModalType(ModalType.None);
        }}
      />
      <AssetVerificationPendingModal
        open={modalType === ModalType.AssetVerificationPending}
        onCancel={() => {
          setModalType(ModalType.None);
          setActiveAsset(undefined);
        }}
        onSubmit={() => {
          if (activeAsset?.type === 'LOGO') {
            setCampaignLogo(activeAsset.attachmentUuid);
          } else if (activeAsset?.type === 'BANNER') {
            setCampaignBanner(activeAsset.attachmentUuid);
          }
          setModalType(ModalType.None);
          setActiveAsset(undefined);
        }}
      />
      {!ASSET_LINK_UPLOAD_ENABLED ? (
        <AssetUploadModal
          open={
            modalType === ModalType.LogoUpload ||
            modalType === ModalType.BannerUpload
          }
          brandUid={brandUid}
          type={modalType === ModalType.LogoUpload ? 'logo' : 'banner'}
          onCancel={() => setModalType(ModalType.None)}
          onUpdate={reloadBrandAssets}
          onSubmit={() => setModalType(ModalType.None)}
        />
      ) : (
        <AssetImageLinkUploadModal
          verifyOnUpload
          assetType={
            modalType === ModalType.LogoUpload
              ? BrandAssetType.Logo
              : BrandAssetType.Banner
          }
          brandUid={brandUid}
          dialogTitle="Asset Upload and Verification"
          dialogDescription={assetUploadDialogDescription}
          panelDescription={assetUploadPanelDescription}
          open={
            modalType === ModalType.LogoUpload ||
            modalType === ModalType.BannerUpload
          }
          confirmBtnText="I Agree"
          onClose={() => {
            setModalType(ModalType.None);
          }}
          onUpdate={reloadBrandAssets}
          onSubmit={() => setModalType(ModalType.None)}
        />
      )}
    </form>
  );
};

export default RcsCampaignDetailsForm;
